import { TreeNode } from '../models/TreeModel';

/**
 * Node types that can have children even if they don't show any initially
 * These nodes will display an expander chevron
 */
export const DYNAMIC_PARENT_NODE_TYPES = [
  'databases',
  'tables',
  'views',
  'stored-procedures',
  'functions',
  'connection',
  'folder'
];

/**
 * Determines if a node type can have children
 */
export function canNodeTypeHaveChildren(nodeType: string): boolean {
  // List of node types that can have children
  const parentNodeTypes = [
    'connection',
    'database',
    'databases-folder',
    'tables-folder',
    'views-folder',
    'programmability-folder',
    'stored-procedures-folder',
    'functions-folder',
    'folder', // Generic folder type
    'dashboards-root' // S23 — conditional "Dashboards" root node
  ];
  
  return parentNodeTypes.includes(nodeType) || nodeType.endsWith('-folder');
}

/**
 * Toggles a node's expanded state
 */
export function toggleNodeExpanded(
  nodeId: string,
  expandedNodeIds: Set<string>
): Set<string> {
  const newExpandedNodeIds = new Set(expandedNodeIds);

  if (expandedNodeIds.has(nodeId)) {
    // We're collapsing this node
    newExpandedNodeIds.delete(nodeId);
    
    // Only collapse children that start with this node's ID followed by a colon
    // This ensures we only collapse actual descendants
    Array.from(expandedNodeIds)
      .filter(id => id.startsWith(`${nodeId}:`))
      .forEach(id => {
        newExpandedNodeIds.delete(id);
      });
  } else {
    // We're expanding this node
    newExpandedNodeIds.add(nodeId);
    
    // If this is a child node, make sure its parent is expanded too
    const parts = nodeId.split(':');
    if (parts.length > 1) {
      // For nested paths, ensure all parent paths are expanded
      let currentPath = parts[0];
      newExpandedNodeIds.add(currentPath);
      
      for (let i = 1; i < parts.length - 1; i++) {
        currentPath += `:${parts[i]}`;
        newExpandedNodeIds.add(currentPath);
      }
    }
  }
  
  return newExpandedNodeIds;
}

/**
 * Finds a node by ID in the tree
 */
export function findNodeById(
  nodeId: string,
  nodes: TreeNode[]
): TreeNode | undefined {
  for (const node of nodes) {
    if (node.id === nodeId) {
      return node;
    }
    
    if (node.children?.length) {
      const foundInChildren = findNodeById(nodeId, node.children);
      if (foundInChildren) {
        return foundInChildren;
      }
    }
  }
  
  return undefined;
}

/**
 * Applies expanded states to all nodes in the tree
 */
export function applyExpandedState(
  nodes: TreeNode[],
  expandedNodeIds: Set<string>
): TreeNode[] {
  return nodes.map(node => {
    const isExpanded = expandedNodeIds.has(node.id);
    
    return {
      ...node,
      isExpanded,
      children: node.children && node.children.length > 0
        ? applyExpandedState(node.children, expandedNodeIds)
        : node.children
    };
  });
}

/**
 * Updates a specific node in the tree with the provided updates
 */
export function updateNodeInTree<T extends TreeNode>(
  nodes: T[],
  nodeId: string,
  updates: Partial<T>
): T[] {
  return nodes.map(node => {
    if (node.id === nodeId) {
      // Apply updates to the found node
      return { ...node, ...updates };
    }
    
    // If the node has children, recursively update them
    if (node.children?.length) {
      return {
        ...node,
        children: updateNodeInTree(node.children as T[], nodeId, updates) as any
      };
    }
    
    return node;
  });
}

/**
 * Adds children nodes to a parent node in the tree
 */
export function addChildrenToNodeInTree<T extends TreeNode>(
  nodes: T[],
  parentId: string,
  children: T[]
): T[] {
  return nodes.map(node => {
    if (node.id === parentId) {
      // Add children to the found node
      return {
        ...node,
        children: [...(node.children || []), ...children] as any,
        // If we're adding children, the node definitely can have children
        canHaveChildren: true
      };
    }
    
    // If the node has children, recursively search them
    if (node.children?.length) {
      return {
        ...node,
        children: addChildrenToNodeInTree(node.children as T[], parentId, children) as any
      };
    }
    
    return node;
  });
} 
